import { Button, Container, Typography } from "@material-ui/core"
import {
  getPaymentGetBit,
  getPaymentPaymentMethods,
  postPaymentPaymentDoneBit,
} from "api"
import { useIsDesktop } from "common/MediaQueries"
import GenericButton from "components/GenericButton/GenericButton"
import AiPurchaseHeader from "components/PurchasePage/AiPurchaseHeader"
import { usePurchaseContext } from "components/PurchasePage/PurchaseContext"
import PurchaseHeader from "components/PurchasePage/PurchaseHeader"
import { formatPhoneNumberPayment } from "components/Reusable/Functions/Functions"
import LoaderPopup from "components/Reusable/LoaderPopup"
import moment from "moment"
import { useRouter } from "next/router"
import { useEffect, useMemo, useState } from "react"
import {
  isAndroid,
  isChrome,
  isEdge,
  isFirefox,
  isIOS,
  isOpera,
  isSafari,
} from "react-device-detect"
import styled from "styled-components"
import colors from "theme/colors"
import { PaymentSteps } from "~/pages/payment"
import { usePaymentContext } from "./PaymentContext"
import PaymentSchedulerText from "./PaymentSchedulerText"
import { bitPay, loadBitCom, savePaymentDataToLocalStorage } from "./bitHelpers"

const StyledWrapper = styled(Container)`
  padding-right: 60px;
  margin-top: 120px;
  padding-left: 120px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 94px;
    padding-left: 16px;
    padding-right: 16px;
  }
`

const StyledPaymentChoiceContainer = styled.div`
  margin-top: 20px;
`

const StyledPayments = styled.div<{ $isDesktop: boolean }>`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  flex-direction: column;
  ${(p) => !p.$isDesktop && "justify-content: space-between;"}
`

const StyledPayWithIcon = styled.div`
  direction: ltr;
  display: flex;
  align-items: center;

  font-family: Speedee, NarkisBlock, sans-serif;
`

const StyledButtonText = styled(Typography).attrs({ variant: "body1" })<{
  $bit?: boolean
}>`
  text-align: right;
  font-family: ${(p) => (p.$bit ? "poalimBIT-Medium" : "NarkisBlock")};
  font-size: ${(p) => (p.$bit ? "20px" : "24px")};
  font-weight: ${(p) => (p.$bit ? "500" : "bold")};
  font-stretch: condensed;
  font-style: normal;
  color: ${(p) => (p.$bit ? p.theme.colors.white : p.theme.colors.black)};

  &:hover {
    font-size: 26px;
  }
`

const StyledDivider = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.darkGreen};
  width: 1px;
  height: 25px;
  margin-left: 10px;
  margin-right: 10px;
`

const StyledImg = styled.img<{ $bit?: boolean; $marginLeft?: string }>`
  margin-right: ${(p) => (p.$bit ? "15px" : "5px")};
  margin-left: ${(p) => (p.$marginLeft ? p.$marginLeft : "5px")};
`

const StyledPaymentTitle = styled(Typography).attrs({ variant: "h2" })`
  text-align: right;
  font-family: NarkisBlock;
  font-size: 40px;
  font-weight: bold;
  font-stretch: condensed;
  font-style: normal;
  line-height: 46px;
  letter-spacing: normal;
`

const StyledPaymentSubtitle = styled(Typography).attrs({ variant: "body1" })`
  text-align: right;
  line-height: 26px;
  font-family: Speedee, NarkisBlock;
  font-weight: 400;
`

const StyledButton = styled(GenericButton)<{
  $isBit?: boolean
}>`
  border: ${(p) => (p.$isBit ? "none" : "2px solid #ffb900")};
  background: ${(p) =>
    p.$isBit
      ? "linear-gradient(180deg, #2B5586 0%, #3B96B1 100%)"
      : p.theme.colors.white};
  color: ${({ theme, $isBit }) =>
    $isBit ? theme.colors.white : theme.colors.black};

  ${(p) => p.$isBit && "border-radius: 10px;"}

  ${(p) =>
    p.$isBit &&
    `
    &:hover {
      background: linear-gradient(180deg, #2B5586 0%, #3B96B1 100%);
    }
    `}
`

const NoPaymentWrapper = styled.div`
  text-align: right;
`

const NoPaymentSubtitle = styled(Typography).attrs({ variant: "body1" })`
  font-size: 24px;
  line-height: 30px;
  font-weight: 400;
  text-align: right;
`

const StyledTryAgainButton = styled(Button).attrs({ variant: "contained" })`
  width: 158px;
  height: 48px;
  background-color: ${colors.darkYellow};
  border: none;
  color: ${colors.black};
  font-size: 24px;
  border-radius: 0px;
  line-height: 30px;
  word-break: break-word;
  margin-top: 25px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
    font-size: 24px;
  }
`

type PaymentType = "apple/google" | "creditCard"

const PaymentIframe = () => {
  const isDesktop = useIsDesktop()
  const [loading, setLoading] = useState(false)
  const [availablePayment, setAvailablePayment] = useState(null)

  const {
    purchaseData,
    blessingMode,
    selectedGiftCard,
    giftCardLoadAmount,
    giftCardPrice,
    addPercentage,
    orderId,
    orderRecipientGuid,
    setPaymentInitiationId,
    setTransactionSerialId,
    schedulerType,
  } = usePurchaseContext()

  const { setPaymentSteps } = usePaymentContext()

  const router = useRouter()

  useEffect(() => {
    if (!purchaseData || !selectedGiftCard || !orderId) router.push("/")
  }, [])

  useEffect(() => {
    const getPaymentTypes = async () => {
      const { data } = await getPaymentPaymentMethods(
        orderId,
        isChrome
          ? "chrome"
          : isSafari
          ? "safari"
          : isFirefox
          ? "firefox"
          : isOpera
          ? "opera"
          : isEdge
          ? "edge"
          : "other browser",
        null
      )
      setAvailablePayment(data.body)
    }
    getPaymentTypes()
  }, [])

  const allValuesFalse = useMemo(
    () =>
      availablePayment &&
      Object.values(availablePayment).every((value) => value === false),
    [availablePayment]
  )

  /**
   * This function will be called when a user selects a payment option
   *
   *
   */
  useEffect(() => {
    const storedData = localStorage.getItem(orderRecipientGuid)
    if (storedData) {
      const { expirationTime } = JSON.parse(storedData)
      const currentTime = new Date().getTime()

      if (currentTime > expirationTime) {
        localStorage.removeItem(orderRecipientGuid)
      }
    }
  }, [])

  const handlePaymentSelection = (paymentOption: PaymentType) => {
    if (!paymentOption || !orderId) return
    if (paymentOption === "creditCard") router.push("/payment/creditCard")
    if (paymentOption === "apple/google") router.push("/payment/wallet")
  }

  /**
   * Handling bit payment
   *
   *
   */

  const payBit = (transactionSerialId: string, paymentInitiationId: string) => {
    bitPay({
      transactionSerialId,
      paymentInitiationId,
      onApproval: async () => {
        try {
          setLoading(true)
          await postPaymentPaymentDoneBit({
            paymentInitiationId: paymentInitiationId,
          })
          setLoading(false)
          setPaymentSteps(PaymentSteps.SUCCESS)
        } catch (error) {
          setLoading(false)
          setPaymentSteps(PaymentSteps.FAILURE)
          console.log(error)
        }
      },
      onCancel: async () => {
        try {
          setLoading(true)

          await postPaymentPaymentDoneBit({
            paymentInitiationId: paymentInitiationId,
          })

          setLoading(false)
        } catch (error) {
          setLoading(false)
          console.log(error)
        }
      },
    })
  }

  const getBitPayment = async () => {
    try {
      setLoading(true)
      const { data } = await getPaymentGetBit(
        orderId,
        isChrome
          ? "chrome"
          : isSafari
          ? "safari"
          : isFirefox
          ? "firefox"
          : isOpera
          ? "opera"
          : isEdge
          ? "edge"
          : "other browser"
      )
      setLoading(false)
      if (!isDesktop) {
        savePaymentDataToLocalStorage(orderRecipientGuid, {
          paymentData: data.body,
          purchaseData: purchaseData,
          selectedGiftCard: selectedGiftCard,
          blessingMode: blessingMode,
          giftCardLoadAmount: giftCardLoadAmount,
          giftCardPrice: giftCardPrice,
          addPercentage: addPercentage,
          orderId: orderId,
          orderRecipientGuid: orderRecipientGuid,
        })
      }
      setTransactionSerialId(data.body.transactionSerialId)
      setPaymentInitiationId(data.body.paymentInitiationId)

      loadBitCom({
        isDesktop,
        orderRecipientGuid,
        paymentInitiationId: data.body.paymentInitiationId,
        transactionSerialId: data.body.transactionSerialId,
        isAndroid,
        isIOS,
        pay: payBit,
      })
    } catch (error) {
      setLoading(false)
      console.log(error)
    }
  }
  return (
    <StyledWrapper>
      {selectedGiftCard?.isAiImage ? (
        <AiPurchaseHeader
          purchaseData={purchaseData}
          selectedMode={blessingMode}
          giftCard={selectedGiftCard}
          giftCardPrice={purchaseData?.amount}
          link={"/preview"}
          backIconText={"חזרה"}
          titlePrefix="תשלום - "
          schedulerType={schedulerType}
          addPercentage={addPercentage}
          discountPrice={giftCardPrice}
        />
      ) : (
        <PurchaseHeader
          purchaseData={purchaseData}
          selectedMode={blessingMode}
          giftCard={selectedGiftCard}
          giftCardPrice={giftCardLoadAmount}
          link={"/preview"}
          backIconText={"חזרה"}
          titlePrefix="תשלום - "
          schedulerType={schedulerType}
          addPercentage={addPercentage}
          discountPrice={giftCardPrice}
        />
      )}

      <StyledPaymentChoiceContainer>
        {schedulerType === "Allowance" && (
          <PaymentSchedulerText
            endDate={purchaseData.allowanceEndDateTime}
            scheduleType={purchaseData.giftCardScheduleType}
            startDate={purchaseData.allowanceStartDateTime}
          />
        )}
        {!allValuesFalse ? (
          <>
            <StyledPaymentTitle>סיכום הפרטים:</StyledPaymentTitle>
            <StyledPaymentSubtitle>
              {blessingMode === "full"
                ? purchaseData?.recipientName
                : purchaseData?.senderName}
            </StyledPaymentSubtitle>
            <StyledPaymentSubtitle>
              {formatPhoneNumberPayment(
                blessingMode === "full"
                  ? purchaseData?.phoneNumber
                  : purchaseData?.senderMobilePhone
              )}
            </StyledPaymentSubtitle>
            <StyledPaymentSubtitle>
              {blessingMode === "full"
                ? purchaseData?.recipientEmail
                : purchaseData?.senderEmail}
            </StyledPaymentSubtitle>
            <StyledPaymentSubtitle>
              {schedulerType === "Allowance"
                ? `הגיפטקארד יישלח בתאריך ${moment(
                    purchaseData.allowanceStartDateTime
                  ).format("DD/MM/YYYY")} `
                : schedulerType === "Scheduled"
                ? `הגיפטקארד יישלח בתאריך ${moment(
                    purchaseData.messagesScheduleDate
                  ).format("DD/MM/YYYY")} `
                : " הגיפטקארד יישלח מיד לאחר התשלום"}
            </StyledPaymentSubtitle>

            <StyledPayments $isDesktop={isDesktop}>
              {availablePayment?.creditCard && (
                <StyledButton
                  btnType="outlined"
                  onClick={() => handlePaymentSelection("creditCard")}
                  aria-label="תשלום באמצעות כרטיס אשראי"
                >
                  <StyledPayWithIcon>
                    <StyledButtonText>כרטיס אשראי</StyledButtonText>
                    <StyledImg src="/img/icons/CreditCardLogo.svg" alt="" />
                  </StyledPayWithIcon>
                </StyledButton>
              )}
              {availablePayment?.wallet && (
                <StyledButton
                  btnType="outlined"
                  onClick={() => handlePaymentSelection("apple/google")}
                  aria-label="תשלום באמצעות ארנק דיגיטלי גוגל פיי או אפל פיי"
                >
                  <>
                    <StyledPayWithIcon>
                      <StyledImg
                        src="/img/icons/GooglePayIcon.svg"
                        alt=""
                        $marginLeft={"0px"}
                      />
                      Pay
                    </StyledPayWithIcon>
                    <StyledDivider />
                    <StyledPayWithIcon>
                      <StyledImg src="/img/icons/ApplePayIcon.svg" alt="" />
                      Pay
                    </StyledPayWithIcon>
                  </>
                </StyledButton>
              )}
              {availablePayment?.bit && (
                <StyledButton
                  btnType="outlined"
                  $isBit
                  onClick={() => getBitPayment()}
                  aria-label="תשלום באמצעות ביט"
                >
                  <StyledPayWithIcon>
                    <StyledImg
                      src="/img/icons/bitLogo.svg"
                      alt=""
                      $bit={true}
                    />
                    <StyledButtonText $bit={true}>
                      {" "}
                      תשלום באמצעות
                    </StyledButtonText>
                  </StyledPayWithIcon>
                </StyledButton>
              )}
            </StyledPayments>
          </>
        ) : (
          <NoPaymentWrapper>
            <StyledPaymentTitle>
              אופסי, אי אפשר לבצע רכישה כרגע באתר
            </StyledPaymentTitle>
            <NoPaymentSubtitle>
              אנחנו על זה! בבקשה לנסות שוב בקרוב
            </NoPaymentSubtitle>
            <StyledTryAgainButton onClick={() => router.push("/preview")}>
              בואו ננסה שוב
            </StyledTryAgainButton>
          </NoPaymentWrapper>
        )}
      </StyledPaymentChoiceContainer>
      <LoaderPopup isOpen={loading} />
    </StyledWrapper>
  )
}

export default PaymentIframe
